import React, { useState, useEffect } from "react";
import _ from "lodash";
import "react-datetime/css/react-datetime.css";
import "./DatePickerField.scss";
import ReactDatetime from "react-datetime";
import PropTypes from "prop-types";
import moment from "moment-timezone";

import Button from "../Button/Button";
import TextField from "../TextField/TextField";
import ActionModal from "../ActionModal/ActionModal";
import { CalendarIcon } from "../../icons/CalendarIcon";
import styles from "./DatePickerField.module.scss";
import clsx from "clsx";

const Datetime = ReactDatetime.default || ReactDatetime;

const DatePickerField = React.forwardRef(
  (
    {
      defaultValue,
      errors,
      errorMessages,
      id,
      label,
      labelColor,
      labelFunc,
      placeholder,
      onChange,
      onClear,
      name,
      required,
      dataTestId,
      className,
      disablePast,
      disableFuture,
      minDate,
      maxDate,
      minutesStep,
      autoOk,
      format,
      includeTime,
      disabled,
    },
    ref
  ) => {
    let defaultValueMoment = moment(defaultValue);
    if (_.isEmpty(defaultValue) || !defaultValueMoment.isValid()) {
      defaultValueMoment = null;
    }

    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(defaultValueMoment);
    const [dateSelected, setDateSelected] = useState(value);

    useEffect(() => {
      if (_.isFunction(onChange)) {
        onChange(value);
      }
    }, [value]);

    if (_.isNil(format)) {
      format = includeTime ? "MM/DD/yyyy HH:mm" : "MM/DD/yyyy";
    }

    if (!_.isFunction(labelFunc)) {
      labelFunc = (date, invalidLabel) => {
        const momentDate = moment(date);

        if (momentDate.isValid()) {
          return momentDate.format(format);
        }

        return invalidLabel;
      };
    }

    const isValidDate = (currentDate) => {
      let today = moment();
      const currentDateCopy = moment(currentDate);

      if (includeTime && !_.isNil(dateSelected)) {
        currentDateCopy.hour(dateSelected.hour());
        currentDateCopy.minute(dateSelected.minute());
        currentDateCopy.second(dateSelected.second());
        currentDateCopy.millisecond(dateSelected.millisecond());
      } else {
        today = today.startOf("day");
      }

      const isInvalidDate =
        (currentDateCopy.isBefore(today) && disablePast) ||
        (currentDateCopy.isAfter(today) && disableFuture) ||
        (!_.isNil(minDate) && currentDateCopy.isBefore(minDate)) ||
        (!_.isNil(maxDate) && currentDateCopy.isAfter(maxDate));

      return !isInvalidDate;
    };

    const onDatePick = (date) => {
      setDateSelected(date);

      if (autoOk) {
        setValue(date);
        setIsOpen(false);
      }
    };

    const onOk = () => {
      setValue(dateSelected);
      setIsOpen(false);
    };

    const onClearPressed = () => {
      setValue(null);
      setDateSelected(null);
      setIsOpen(false);
      if (_.isFunction(onClear)) {
        onClear();
      }
    };

    const onDismiss = () => {
      setDateSelected(value);
      setIsOpen(false);
    };

    let modalTitle = "No date selected";
    if (!_.isNil(dateSelected)) {
      let modalFormat = "ddd, MMM DD, YYYY";
      if (includeTime) {
        modalFormat += " h:mm A";
      }

      modalTitle = dateSelected.format(modalFormat);
    }

    const renderPicker = (viewMode, renderDefault) => {
      return <div className={styles.calendar}>{renderDefault()}</div>;
    };

    const actionBarClassName = clsx("d-flex", "align-items-center", "justify-content-between");

    return (
      <div className={clsx(styles.datePickerField, className)}>
        <TextField
          className={styles.input}
          placeholder={placeholder}
          label={label}
          labelColor={labelColor}
          id={id}
          name={name}
          errors={errors}
          errorMessages={errorMessages}
          ref={ref}
          icon={<CalendarIcon />}
          readOnly
          required={required}
          inputProps={{
            onClick: () => setIsOpen(true),
            value: value ? labelFunc(value, "Invalid date") : "",
          }}
          data-testid={dataTestId}
          disabled={disabled}
        />
        <ActionModal
          isOpen={isOpen}
          title={modalTitle}
          onDismiss={onDismiss}
          className={styles.calendarModal}
          center
          modalProps={{ ariaHideApp: false }}
        >
          <Datetime
            value={dateSelected}
            isValidDate={isValidDate}
            onChange={onDatePick}
            dateFormat="MMM DD, YYYY"
            timeFormat={!!includeTime}
            initialViewMode="days"
            timeConstraints={{ minutes: { step: minutesStep }, milliseconds: { step: 1 } }}
            closeOnSelect={autoOk}
            renderView={renderPicker}
            input={false}
            open
          />
          <div className={actionBarClassName}>
            <Button
              label="Clear"
              onClick={onClearPressed}
              disabled={_.isNil(value)}
              type="PrimaryInverse"
              className={styles.actionButton}
            />
            <Button
              label="Ok"
              onClick={onOk}
              disabled={_.isNil(dateSelected)}
              type="Primary"
              className={styles.actionButton}
            />
          </div>
        </ActionModal>
      </div>
    );
  }
);

DatePickerField.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
  }),
  errorMessages: PropTypes.object,
  label: PropTypes.string,
  labelColor: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  dataTestId: PropTypes.string,
  format: PropTypes.string,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
  autoOk: PropTypes.bool,
  minutesStep: PropTypes.number,
};

DatePickerField.defaultProps = {
  defaultValue: null,
  disabled: false,
  errors: {},
  errorMessages: {},
  label: "",
  name: "",
  placeholder: "",
  readOnly: false,
  required: false,
  dataTestId: "DatePickerField",
  disablePast: false,
  disableFuture: false,
  autoOk: false,
  minutesStep: 1,
};

export default DatePickerField;
